import type { MarketsApi } from "@/v2/api/metadata/markets";
import type { ContentTab, DialogTabSaveContext } from "@/v2/framework/dialog/types";
import { mergeEntityPatches } from "@/v2/framework/utils/mergeEntityPatches";
import { createInheritableTabSave } from "@/v2/kits/tenant-inheritance";
import {
  marketOutcomesActivityOptions,
  marketStatusOptions,
  marketTypeOptions,
} from "../../../../shared";
import {
  buildMarketSupplementalNamesPatch,
  buildSpecifiersPatch,
  syncMarketSpecifiersBaseline,
  syncMarketSupplementalBaseline,
} from "../../market-fields";
import { marketId, notifySuccess } from "../shared";

const mainTabChildren: ContentTab["children"] = [
  {
    type: "text-field",
    name: "name",
    label: "Name",
    placeholder: "Enter name",
    inheritance: { computedKey: "computedName", parentKey: "parentName" },
    validate: (value, values) => {
      if (values.isTenantSourceOfEntity && !String(value ?? "").trim()) return "Required";
      return undefined;
    },
  },
  {
    type: "text-field",
    name: "eventMarketName",
    label: "Event market name",
    placeholder: "Enter event market name",
    inheritance: { computedKey: "computedEventMarketName", parentKey: "parentEventMarketName" },
    validate: (value, values) => {
      if (values.isTenantSourceOfEntity && !String(value ?? "").trim()) return "Required";
      return undefined;
    },
  },
  {
    type: "text-field",
    name: "sportName",
    label: "Sport",
  },
  {
    type: "select-field",
    name: "type",
    label: "Market type",
    options: marketTypeOptions,
    disabled: true,
  },
  {
    type: "select-field",
    name: "status",
    label: "Status",
    required: true,
    placeholder: "Select status",
    options: marketStatusOptions,
    inheritance: { computedKey: "computedStatus", parentKey: "parentStatus" },
  },
  {
    type: "text-field",
    name: "info",
    label: "Info",
    placeholder: "Enter info",
    inheritance: { computedKey: "computedInfo", parentKey: "parentInfo" },
  },
  {
    type: "select-field",
    name: "marketOutcomesActivityDisplayType",
    label: "Outcomes activity display",
    options: marketOutcomesActivityOptions,
    inheritance: {
      computedKey: "computedMarketOutcomesActivityDisplayType",
      parentKey: "parentMarketOutcomesActivityDisplayType",
    },
  },
];

export function createMainTab(marketsApi: MarketsApi): ContentTab {
  const save = createInheritableTabSave({
    children: mainTabChildren,
    buildExtraPatch: (ctx) =>
      mergeEntityPatches(
        buildMarketSupplementalNamesPatch(ctx.values, ctx.baseline),
        buildSpecifiersPatch(ctx.values, ctx.baseline),
      ),
    afterPersist: (ctx) => {
      syncMarketSupplementalBaseline(ctx);
      syncMarketSpecifiersBaseline(ctx);
    },
    persist: async (patch, ctx: DialogTabSaveContext) => {
      await marketsApi.update(marketId(ctx.values), patch);
      notifySuccess();
    },
  });

  return {
    id: "main",
    label: "Main",
    save,
    children: [
      ...mainTabChildren,
      {
        type: "component",
        component: () => import("../../MarketSupplementalNamesFields.vue"),
        props: { mode: "edit" },
      },
      {
        type: "component",
        component: () => import("../../MarketSpecifiersFields.vue"),
        props: { mode: "edit", marketsApi },
      },
    ],
  };
}
